import { useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { useBoardFetch } from "./useBoardFetch";
import {
  selectSubGroupingDataMemoized,
  selectGroupingDataMemoized,
  selectSubGroupData,
  selectGroupingConfig,
} from "@/features/bughurd/boardSelector";
import { setSubGroupData, setInitialLoading } from "@/features/bughurd/slice";
import { useBoardFilters } from "./useBoardFilters";

export const useSubGroupOperations = () => {
  const dispatch = useAppDispatch();
  const { fetchBugsForGroup } = useBoardFetch();

  const subGroupingData = useAppSelector(selectSubGroupingDataMemoized);
  const groupingData = useAppSelector(selectGroupingDataMemoized);
  const subGroupData = useAppSelector(selectSubGroupData);
  const groupingConfig = useAppSelector(selectGroupingConfig);
  const { subGroupBy } = groupingConfig;

  const {
    selectedTags,
    selectedAvatars,
    selectedStatuses,
    selectedPriorities,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
  } = useBoardFilters();

  const PAGE_LIMIT = 10;

  // Enhanced fetchBugsForSubGroups function
  const fetchBugsForSubGroups = useCallback(async () => {
    if (subGroupBy === "None") {
      return;
    }

    if (Object.keys(subGroupingData).length === 0) {
      return;
    }

    if (Object.keys(groupingData).length === 0) {
      return;
    }

    try {
      dispatch(setInitialLoading(true));

      // Clear existing sub-group data to start fresh
      dispatch(setSubGroupData({}));

      // Create fetch promises for all sub-group/group combinations
      const fetchPromises: Promise<void>[] = [];

      Object.keys(subGroupingData).forEach((subGroupId) => {
        const subGroupInfo = subGroupingData[subGroupId];

        Object.keys(groupingData).forEach((groupId) => {
          const groupInfo = groupingData[groupId];

          // Create fetch promise for this sub-group/group combination
          const fetchPromise = fetchBugsForGroup(
            groupId,
            groupInfo,
            1,
            PAGE_LIMIT,
            false, // append = false for initial fetch
            false, // forceFetch = false
            subGroupId,
            subGroupInfo
          ).catch((error) => {
            console.error(
              `Error fetching data for sub-group ${subGroupInfo.title} - group ${groupInfo.title}:`,
              error
            );
          });

          fetchPromises.push(fetchPromise);
        });
      });

      // Wait for all API calls to complete
      await Promise.allSettled(fetchPromises);
    } catch (error) {
      console.error("Error in fetchBugsForSubGroups:", error);
    } finally {
      dispatch(setInitialLoading(false));
    }
  }, [
    subGroupingData,
    groupingData,
    subGroupData,
    selectedPriorities,
    selectedAvatars,
    selectedStatuses,
    selectedTags,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    isRecurring,
    updatedAtStart,
    updatedAtEnd,
    subGroupBy,
  ]);

  return {
    fetchBugsForSubGroups,
  };
};
